import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LOGO_WHITE } from "../lib/media";
import { openCookieSettings } from "./CookieConsent";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, source: "footer" });
      toast.success("You're on the list. Fresh updates incoming.");
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-10 bg-[#1A1A1A] text-[#F7F5F0]" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <img loading="lazy" decoding="async" src={LOGO_WHITE} alt="SugaSpice" className="h-24 w-auto" />
            <p className="font-display text-4xl md:text-5xl mt-8 leading-[0.95] tracking-tight">
              Fresh bakes.<br />Big flavour.<br />
              <span className="text-[#C8102E]">Made to crave.</span>
            </p>
            <p className="mt-6 max-w-md text-[#C9C5BE] text-sm leading-relaxed">
              SugaSpice Bakery & Kitchen — premium artisan bakery, hot kitchen, coffee and street-food
              inspired flavours. Now open in St Neots, Huntingdonshire.
            </p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs tracking-[0.22em] uppercase text-[#A3A3A3] mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              {[
                ["Menu", "/menu"],
                ["Order Online", "/order"],
                ["Offers", "/offers"],
                ["Wholesale", "/wholesale"],
                ["Visit Us", "/visit"],
                ["About", "/about"],
                ["Journal", "/blog"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-[#D9D6CF] hover:text-[#F7F5F0] transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-xs tracking-[0.22em] uppercase text-[#A3A3A3] mb-5">Get fresh drops</h4>
            <p className="text-sm text-[#C9C5BE] mb-5">
              Get SugaSpice news, offers and fresh drops by email. New menu launches, seasonal
              specials and openings — direct to your inbox.
            </p>
            <form onSubmit={onSubscribe} className="flex gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                aria-label="Your email address"
                className="flex-1 bg-transparent border border-[#3a3a3a] rounded-full px-5 py-3 text-sm placeholder:text-[#7a7a7a] focus:border-[#C8102E] focus:outline-none"
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] disabled:opacity-60"
                data-testid="footer-newsletter-submit"
              >
                {loading ? "..." : "Join"}
              </button>
            </form>
            <p className="mt-3 text-xs text-[#9a968f] leading-relaxed">
              You can unsubscribe at any time. See our{" "}
              <Link to="/privacy" className="underline hover:text-[#C9C5BE]">Privacy Policy</Link>.
            </p>
            <div className="mt-8 space-y-2 text-sm text-[#C9C5BE]">
              <p className="flex items-center gap-2"><Mail className="h-4 w-4" /> sugaspicebakery01@gmail.com</p>
              <p>10 Moores Walk, St Neots, PE19 1AG</p>
            </div>
            <div className="mt-6 flex gap-3">
              <a href="https://www.instagram.com/sugaspicebakerykitchen/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#1A1A1A] transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/people/SugaSpice/61592682779212/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 hover:bg-white hover:text-[#1A1A1A] transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#C9C5BE] hover:text-[#F7F5F0] transition-colors">Privacy Policy</Link>
            <span className="text-white/20">|</span>
            <Link to="/cookies" data-testid="footer-cookie-policy-link" className="text-[#C9C5BE] hover:text-[#F7F5F0] transition-colors">Cookie Policy</Link>
            <span className="text-white/20">|</span>
            <button type="button" onClick={openCookieSettings} data-testid="footer-cookie-settings" className="text-[#C9C5BE] hover:text-[#F7F5F0] transition-colors">Cookie Settings</button>
          </div>

          <p className="mt-5 text-xs text-[#8a8a8a] leading-relaxed max-w-3xl">
            {/* Registered legal details (name, company no., registered office) to be added once confirmed. */}
            SugaSpice Bakery &amp; Kitchen, 10 Moores Walk, St Neots, PE19 1AG.
          </p>

          <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#8a8a8a]">
            <p>© {new Date().getFullYear()} SugaSpice Bakery &amp; Kitchen. All rights reserved.</p>
            <a
              href="https://www.brightlabs.live"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-brightlabs-credit"
              className="group inline-flex items-center gap-2 hover:text-[#F7F5F0] transition-colors"
            >
              <span>Made by</span>
              <img loading="lazy" decoding="async" src="/brand/brightlabs-mark.png" alt="BrightLabs" className="h-4 w-4 object-contain" />
              <span className="font-semibold tracking-wide text-[#C9C5BE] group-hover:text-white transition-colors">BrightLabs</span>
            </a>
            <p className="tracking-[0.18em] uppercase">St Neots · Huntingdonshire · UK</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
